import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { DateTime } from 'luxon';
import { Card, CustomPaginator, GenericTable, GenericTableProps } from '@cdaas/reusable-components-daas';
import { AdminProductOrder, AdminProductOrderListResponse, OrderType } from '@cdaas/api-clients/dist/order-admin-openapi';
import { useTranslation } from 'react-i18next';

import { orderAdminProductOrderClient } from '../../../../api-client/order-admin-client';
import { DATE_AND_TIME_FORMAT } from '../../../../utils';
import PageTemplate, { PageTemplateTranslations } from '../PageTemplate/PageTemplate';
import { DropdownMultiselect, OptionListOption } from '@gaiads/telia-react-component-library';
import { debounce } from 'lodash';

export default function ReturnPackageOrdersPage() {
  const { t } = useTranslation('backOffice');
  const pageTemplateTranslations: PageTemplateTranslations = {
    title: t('returnPackageOrdersPage.title'),
  };

  const optionList: OptionListOption[] = [
    {
      label: t('common.orderStatus.cannotBeProcessed'),
      value: 'ERROR',
      checked: true,
    },
    {
      label: t('common.orderStatus.processingOrder'),
      value: 'CREATED,LOGISTICS_ORDER_SUBMITTED,SUPPORT_ORDER_CREATED,SUPPORT_ORDER_SUBMITTED',
    },
    {
      label: t('common.orderStatus.delivered'),
      value: 'LOGISTICS_ORDER_COMPLETED,ORDER_COMPLETE,ORDER_UPDATED_AFTER_COMPLETION',
    },
    {
      label: t('common.orderStatus.cancelled'),
      value: 'CANCELLED',
    },
    {
      label: t('common.orderStatus.returned'),
      value: 'RETURNED',
    },
  ];

  const [options, setOptions] = useState<OptionListOption[]>(optionList);

  const [ordersResponse, setOrdersResponse] = useState<AdminProductOrderListResponse | undefined>();
  const [ordersLoading, setOrdersLoading] = useState<GenericTableProps<AdminProductOrder>['loading']>('short');
  const [ordersApiError, setOrdersApiError] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState<number>(0);

  const history = useHistory();

  const statusLabels = () => {
    const statusLabelList = options.filter(option => option.checked).map(option => option.label);
    return statusLabelList.length > 0 ? statusLabelList.join(', ') : t('common.orderStatus.all');
  };

  const fetchOrders = async (page: number, orderStatusList: string[]): Promise<void> => {
    try {
      setOrdersLoading('short');
      const orders = (
        await orderAdminProductOrderClient.listProductOrder(
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          orderStatusList.length > 0 ? orderStatusList : undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          OrderType.ReturnPackage,
          undefined,
          page,
          10,
        )
      ).data;
      setOrdersResponse(orders);
    } catch {
      setOrdersApiError(true);
    }
    setOrdersLoading('none');
  };

  const debouncedFetchReturnPackageOrders = useMemo(() => debounce(fetchOrders, 1000), []);

  useEffect(() => {
    debouncedFetchReturnPackageOrders(
      pageNumber,
      options.filter(option => option.checked).flatMap(option => option.value.split(',')),
    );
  }, [debouncedFetchReturnPackageOrders, pageNumber, options]);

  const changeOptions = (newOptionList: OptionListOption[]) => {
    setPageNumber(0);
    setOptions(newOptionList);
  };

  return (
    <PageTemplate
      translations={pageTemplateTranslations}
      pageContent={
        <>
          <DropdownMultiselect
            label={t('common.orderStatus.select')}
            onChange={(event, selectedOption) => {
              const newOptionList = options.map(option => {
                if (option.value === selectedOption.value) {
                  return { ...option, checked: !option.checked };
                } else {
                  return option;
                }
              });
              changeOptions(newOptionList);
            }}
            onClear={() => {
              changeOptions(optionList);
            }}
            options={options}
            disabled={ordersLoading !== 'none'}
          />
          <Card
            title={`${t('returnPackageOrdersPage.cardTitle')}: ${statusLabels()}`}
            wrapperDivCustomProps={{ backgroundColor: 'white', padding: { size: 'lg' }, borderRadius: { size: 'md' } }}
            tag={
              ordersResponse?.contents?.length
                ? { text: t('returnPackageOrdersPage.tagCheck') }
                : ordersLoading !== 'none'
                  ? { text: t('returnPackageOrdersPage.tagLoading'), tagProps: { backgroundColor: 'orange400' } }
                  : undefined
            }
            isInitiallyOpen={true}
          >
            <GenericTable
              isGenericTableInsideCard={true}
              noDataText={t('returnPackageOrdersPage.table.noDataText')}
              errorText={ordersApiError ? t('returnPackageOrdersPage.table.errorText') : undefined}
              loading={ordersLoading}
              inputData={ordersResponse?.contents}
              rowConfigs={[
                {
                  header: t('returnPackageOrdersPage.table.headerOrganization'),
                  key: 'ordered_to.customer_name',
                  cellWidthMultiplier: 2,
                },
                {
                  header: t('returnPackageOrdersPage.table.headerOrderId'),
                  key: 'unique_id',
                  cellWidthMultiplier: 2,
                },
                {
                  header: t('returnPackageOrdersPage.table.headerColtOrderId'),
                  key: 'colt_hardware_delivery_id',
                },
                {
                  header: t('returnPackageOrdersPage.table.headerCreated'),
                  key: 'created',
                  formatValue(value) {
                    return DateTime.fromISO(value).toFormat(DATE_AND_TIME_FORMAT);
                  },
                },
              ]}
              buttonConfig={{
                name: t('returnPackageOrdersPage.table.rowButtonText'),
                buttonProps: {
                  iconName: 'chevron-right',
                  size: 'md',
                  iconOnRight: true,
                },
                buttonClickCallback: order =>
                  history.push(`/customer/${order.ordered_to.external_organization_id}/devices/${order.packages[0].asset_id}`),
              }}
            />
            {!!ordersResponse?.page_metadata && ordersLoading === 'none' && (
              <CustomPaginator
                activatePageInfoObject={ordersResponse.page_metadata}
                onPageChange={pageChangeInfo => setPageNumber(pageChangeInfo.nextChosenPageNumber)}
                paginatorStyleProps={{
                  pageSelectorProps: { centered: false },
                }}
              />
            )}
          </Card>
        </>
      }
    />
  );
}
